import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { ROUTES } from 'src/app/data/routing-navigation.data';
import { RoutingActions } from 'src/app/state/actions/routing.actions';
import { selectCurrentRoute } from 'src/app/state/selectors/routing.selectors';
import { FullSizeLoadingService } from '../utils/full-size-loading.service';

/**
 * El token de evaluación solo se puede reemitir con un OTP nuevo: el código que
 * se capturó en el paso 3 ya se consumió al emitirlo la primera vez, así que
 * reutilizarlo hace que `evaluation_steps_auths` responda 403 y el proceso
 * termine en la pantalla de rechazo.
 *
 * Este servicio manda al socio de vuelta a la pantalla de verificación —donde
 * `cmv-enter-code` dispara `getOtp` solo por montarse— y recuerda la pantalla
 * desde la que salió para devolverlo ahí en cuanto el OTP quede validado.
 *
 * Se recupera la pantalla, no la acción en curso: la petición que provocó la
 * reautenticación se descarta y el socio la vuelve a disparar al volver.
 */
@Injectable({ providedIn: 'root' })
export class OtpReauthenticationService {
	private routeToResume: string | null = null;

	constructor(
		private store: Store,
		private router: Router,
		private fullSizeLoadingService: FullSizeLoadingService
	) {}

	get isPending(): boolean {
		return this.routeToResume !== null;
	}

	/**
	 * Devuelve `false` cuando no hay una pantalla a la que valga la pena volver
	 * (no se conoce la ruta actual, o el socio ya está en la de verificación). En
	 * ese caso quien llama debe tratarlo como un fallo y no como una
	 * reautenticación en curso.
	 */
	start(): boolean {
		if (this.isPending) {
			return true;
		}
		const currentRoute = this.readCurrentRoute();
		if (!currentRoute || currentRoute === ROUTES.CREDIT_HISTORY_CONSULT) {
			return false;
		}
		this.routeToResume = currentRoute;
		this.fullSizeLoadingService.close();
		this.navigateTo(ROUTES.CREDIT_HISTORY_CONSULT);
		return true;
	}

	/** Vuelve a la pantalla desde la que se pidió el código nuevo. */
	resume(): boolean {
		const routeToResume = this.routeToResume;
		if (!routeToResume) {
			return false;
		}
		this.routeToResume = null;
		this.navigateTo(routeToResume);
		return true;
	}

	cancel(): void {
		this.routeToResume = null;
	}

	private readCurrentRoute(): string {
		let currentRoute = '';
		this.store
			.select(selectCurrentRoute)
			.pipe(take(1))
			.subscribe(route => {
				currentRoute = route;
			});
		return currentRoute;
	}

	/**
	 * `CorrectNavigationGuard` solo deja pasar una ruta que ya esté fijada en el
	 * store `routing`, así que se registra antes de navegar.
	 */
	private navigateTo(route: string): void {
		this.store.dispatch(
			RoutingActions.addCurrentRoute({ currentRoute: route })
		);
		this.router.navigateByUrl(route);
	}
}
